import { Directive } from 'vue'

export interface Focal {
  focusX: number
  focusY: number
  zoom: number
}

// Places an <img> so its focal point (focusX/focusY as fractions of the *image*) sits at the
// centre of its wrapper, scaled to cover the wrapper, times zoom (>= 1), and clamped so the
// wrapper stays fully covered (no gaps). The focal point is centred exactly when zoom leaves
// room; otherwise it's pulled as close to centre as covering allows.
//
// This can't be pure CSS: it needs the image's intrinsic size (which CSS won't expose to
// author math) plus the wrapper's size. So it's computed here from measured values.
// The wrapper must be `position: relative; overflow: hidden` with a definite size.
export function focalStyle(iw: number, ih: number, fw: number, fh: number, focal: Focal) {
  if (!iw || !ih || !fw || !fh) {
    return null
  }
  const zoom = Number.isFinite(focal.zoom) ? Math.max(1, focal.zoom) : 1
  const scale = Math.max(fw / iw, fh / ih) * zoom
  const dw = iw * scale
  const dh = ih * scale
  const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value))
  // Translate the image so the focal point lands at the wrapper centre, clamped to [cover, 0]
  // per axis so an edge never crosses into the wrapper.
  const tx = clamp(fw / 2 - focal.focusX * dw, fw - dw, 0)
  const ty = clamp(fh / 2 - focal.focusY * dh, fh - dh, 0)
  return {
    position: 'absolute',
    left: '0',
    top: '0',
    width: `${iw}px`,
    height: `${ih}px`,
    maxWidth: 'none',
    transformOrigin: '0 0',
    transform: `translate(${tx}px, ${ty}px) scale(${scale})`,
  }
}

export const MAX_ZOOM = 20

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value))
}

// The clamped translate focalStyle renders for a focal point (the inverse of the render).
function coverTranslate(focal: Focal, iw: number, ih: number, fw: number, fh: number) {
  const scale = Math.max(fw / iw, fh / ih) * Math.max(1, focal.zoom)
  const dw = iw * scale
  const dh = ih * scale
  return {
    dw,
    dh,
    tx: clamp(fw / 2 - focal.focusX * dw, fw - dw, 0),
    ty: clamp(fh / 2 - focal.focusY * dh, fh - dh, 0),
  }
}

// Pan by a cursor delta: move the image's rendered translate, then read the focal point back
// from where it settled. The clamp means the focal value only shifts while the image can still
// move (no invisible drift once an edge is pinned).
export function panFocal(focal: Focal, dx: number, dy: number, iw: number, ih: number, fw: number, fh: number) {
  const { dw, dh, tx, ty } = coverTranslate(focal, iw, ih, fw, fh)
  const nx = clamp(tx + dx, fw - dw, 0)
  const ny = clamp(ty + dy, fh - dh, 0)
  return {
    focusX: clamp((fw / 2 - nx) / dw, 0, 1),
    focusY: clamp((fh / 2 - ny) / dh, 0, 1),
  }
}

// Zoom to newZoom while keeping the image point under (px, py) — cursor position in frame px —
// fixed, so scrolling over a face zooms into that face.
export function zoomFocalToward(focal: Focal, newZoom: number, px: number, py: number, iw: number, ih: number, fw: number, fh: number) {
  const zoom = clamp(newZoom, 1, MAX_ZOOM)
  const { dw, dh, tx, ty } = coverTranslate(focal, iw, ih, fw, fh)
  const imgX = (px - tx) / dw
  const imgY = (py - ty) / dh
  const scale = Math.max(fw / iw, fh / ih) * zoom
  const dw2 = iw * scale
  const dh2 = ih * scale
  return {
    focusX: clamp(imgX + (fw / 2 - px) / dw2, 0, 1),
    focusY: clamp(imgY + (fh / 2 - py) / dh2, 0, 1),
    zoom,
  }
}

interface FocalImg extends HTMLImageElement {
  _focal?: Focal
  _focalObserver?: ResizeObserver
  _focalOnLoad?: () => void
  _focalRaf?: number
}

function place(img: FocalImg) {
  if (img._focalRaf) {
    cancelAnimationFrame(img._focalRaf)
    img._focalRaf = undefined
  }
  attempt(img, 0)
}

function attempt(img: FocalImg, tries: number) {
  if (!img._focal) {
    return
  }
  // The image is position:absolute (out of flow), so measure the wrapper, not the image.
  const wrapper = img.parentElement
  const fw = wrapper ? wrapper.clientWidth : img.clientWidth
  const fh = wrapper ? wrapper.clientHeight : img.clientHeight
  const style = focalStyle(img.naturalWidth, img.naturalHeight, fw, fh, img._focal)
  if (style) {
    Object.assign(img.style, style)
    return
  }
  // Wrapper isn't measurable yet — image still loading, or it's inside a modal that was
  // display:none when mounted. Retry for a short while; the ResizeObserver also re-runs
  // once the wrapper gets a real size.
  if (tries < 60) {
    img._focalRaf = requestAnimationFrame(() => attempt(img, tries + 1))
  }
}

// Usage: <img v-focal="{ focusX, focusY, zoom }"/> inside a sized, position:relative,
// overflow:hidden wrapper. Re-runs on image load, wrapper resize, and value change. A nullish
// value is a no-op (the image just keeps its CSS default), so optional focals are fine.
export const vFocal: Directive<FocalImg, Focal | undefined> = {
  mounted(img, binding) {
    img._focal = binding.value
    const run = () => place(img)
    img._focalOnLoad = run
    img.addEventListener('load', run)
    const observer = new ResizeObserver(run)
    observer.observe(img.parentElement ?? img)
    img._focalObserver = observer
    if (img.complete && img.naturalWidth) {
      run()
    }
  },
  updated(img, binding) {
    img._focal = binding.value
    place(img)
  },
  unmounted(img) {
    img._focalObserver?.disconnect()
    if (img._focalRaf) {
      cancelAnimationFrame(img._focalRaf)
    }
    if (img._focalOnLoad) {
      img.removeEventListener('load', img._focalOnLoad)
    }
  },
}
